import React from 'react'
import { Button } from '@/shared/ui'
import LoadingCandles from '@/shared/ui/LoadingCandles'
import type { AnalysisItem } from '../services/analysisService'
import { AnalysisCard } from './AnalysisCard'
import { AnalysisLockedCard } from './AnalysisLockedCard'
import { useAnalysisCommunitySummary } from '../hooks/useAnalysisCommunitySummary'
import { useCommunitySummary } from '@/modules/comunidade/hooks/useCommunitySummary'
import { useLinkedOpportunitiesMap } from '../hooks/useLinkedOpportunitiesMap'
import { useThermometerGate } from '@/modules/termometro/hooks/useThermometerGate'

const LIST_BATCH = 6
const AUTOLOAD_ROOT_MARGIN = '160px 0px 0px 0px'

type AnalysisListProps = {
  items: AnalysisItem[]
  communityEnabled: boolean
  canManagePosts: boolean
  focusAnalysisId?: number | null
  focusMessageId?: number | null
  hasMore: boolean
  loadingMore: boolean
  onLoadMore?: () => void
  onReload?: () => void
}

export function AnalysisList({
  items,
  communityEnabled,
  canManagePosts,
  focusAnalysisId,
  focusMessageId,
  hasMore,
  loadingMore,
  onLoadMore,
  onReload,
}: AnalysisListProps) {
  const [visibleCount, setVisibleCount] = React.useState(LIST_BATCH)
  const prevTotalRef = React.useRef(0)
  const sentinelRef = React.useRef<HTMLDivElement | null>(null)
  const [autoLoadEnabled, setAutoLoadEnabled] = React.useState(true)

  React.useEffect(() => {
    const total = items.length
    const prevTotal = prevTotalRef.current
    prevTotalRef.current = total
    if (!total) return

    setVisibleCount((current) => {
      if (!current) return Math.min(LIST_BATCH, total)
      if (current > total) return total
      if (prevTotal > 0 && total > prevTotal && current >= prevTotal) {
        return Math.min(current + LIST_BATCH, total)
      }
      return current
    })
  }, [items.length])

  React.useEffect(() => {
    const focusId = focusAnalysisId != null ? Number(focusAnalysisId) : null
    if (!focusId || !Number.isFinite(focusId) || focusId <= 0) return

    const idx = items.findIndex((it) => Number(it.id) === focusId)
    if (idx < 0) return

    const required = idx + 1

    setVisibleCount((current) =>
      current >= required ? current : Math.min(required, items.length),
    )
  }, [focusAnalysisId, items])

  const visibleItems = React.useMemo(
    () => items.slice(0, visibleCount),
    [items, visibleCount],
  )
  const visibleAllowedItems = React.useMemo(
    () => visibleItems.filter((it) => it.canView),
    [visibleItems],
  )
  const hasMoreToShow = visibleCount < items.length
  const showLoadMore = hasMoreToShow || (hasMore && !!onLoadMore)

  const { thermoAllowed, noteForMaster } = useThermometerGate()

  const visibleAnalysisThreadIds = React.useMemo(
    () =>
      visibleAllowedItems
        .filter((it) => it.linkedOpportunityId === null)
        .map((it) =>
          it.linkedTimelineRootId != null
            ? Number(it.linkedTimelineRootId)
            : Number(it.id),
        )
        .filter((id) => Number.isFinite(id) && id > 0),
    [visibleAllowedItems],
  )

  const timelineRootIdsWithLinks = React.useMemo(() => {
    const ids = new Set<number>()
    visibleAllowedItems.forEach((it) => {
      if (it.linkedTimelineRootId != null) {
        const rootId = Number(it.linkedTimelineRootId)
        if (Number.isFinite(rootId) && rootId > 0) {
          ids.add(rootId)
        }
      }
    })
    return ids
  }, [visibleAllowedItems])

  const visibleLinkedOpportunityIds = React.useMemo(
    () =>
      visibleAllowedItems
        .map((it) => it.linkedOpportunityId)
        .filter((id): id is number => Number.isFinite(Number(id)) && Number(id) > 0)
        .map((id) => Math.floor(Number(id))),
    [visibleAllowedItems],
  )

  const { map: analysisCommunitySummaryMap } = useAnalysisCommunitySummary(
    visibleAnalysisThreadIds,
    communityEnabled,
  )

  const { map: opportunityCommunitySummaryMap } = useCommunitySummary(
    visibleLinkedOpportunityIds,
    communityEnabled,
  )

  const { map: linkedOpportunitiesMap } = useLinkedOpportunitiesMap(
    visibleLinkedOpportunityIds,
    visibleLinkedOpportunityIds.length > 0,
  )

  const handleLoadMore = React.useCallback(() => {
    if (loadingMore) return

    if (visibleCount < items.length) {
      setVisibleCount((current) => Math.min(current + LIST_BATCH, items.length))
      return
    }

    if (!hasMore) return
    onLoadMore?.()
  }, [hasMore, items.length, loadingMore, onLoadMore, visibleCount])

  React.useEffect(() => {
    if (!showLoadMore) return
    if (!autoLoadEnabled) return

    const el = sentinelRef.current
    if (!el) return
    if (typeof IntersectionObserver === 'undefined') {
      setAutoLoadEnabled(false)
      return
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            handleLoadMore()
          }
        })
      },
      { rootMargin: AUTOLOAD_ROOT_MARGIN },
    )

    observer.observe(el)

    return () => {
      observer.disconnect()
    }
  }, [autoLoadEnabled, handleLoadMore, showLoadMore])

  if (!items.length) {
    return (
      <div className="mt-6 text-center text-sm text-foreground/70">
        Nenhuma análise publicada ainda. Use o botão &quot;Adicionar análise&quot; para criar a primeira.
      </div>
    )
  }

  return (
    <div className="mt-4 space-y-4">
      {visibleItems.map((item) => {
        if (!item.canView) {
          return (
            <AnalysisLockedCard
              key={item.id}
              requiredPlanName={item.requiredPlan?.name || null}
              peopleCount={item.peopleCount}
            />
          )
        }
        const linkedId = item.linkedOpportunityId
        const linkedOpportunity =
          linkedId !== null ? linkedOpportunitiesMap[String(linkedId)] ?? null : null

        const analysisThreadId =
          linkedId === null
            ? item.linkedTimelineRootId != null
              ? Number(item.linkedTimelineRootId)
              : Number(item.id)
            : null

        const analysisCommunitySummary =
          linkedId === null && analysisThreadId != null
            ? analysisCommunitySummaryMap?.[String(analysisThreadId)] ?? null
            : null

        const opportunityCommunitySummary =
          linkedId !== null
            ? opportunityCommunitySummaryMap?.[String(linkedId)] ?? null
            : null

        const effectiveFocusMessageId =
          linkedId === null && focusAnalysisId && Number(item.id) === Number(focusAnalysisId)
            ? focusMessageId ?? null
            : null

        const showTimelineOverlay =
          linkedId === null &&
          (item.linkedTimelineRootId != null || timelineRootIdsWithLinks.has(Number(item.id)))

        return (
          <AnalysisCard
            key={item.id}
            analysis={item}
            communityEnabled={communityEnabled}
            canManagePosts={canManagePosts}
            thermoAllowed={thermoAllowed}
            noteForMaster={noteForMaster}
            linkedOpportunity={linkedOpportunity}
            analysisCommunitySummary={analysisCommunitySummary}
            opportunityCommunitySummary={opportunityCommunitySummary}
            focusMessageId={effectiveFocusMessageId}
            timelineRootId={analysisThreadId}
            showTimelineOverlay={showTimelineOverlay}
            onChanged={onReload}
          />
        )
      })}

      {showLoadMore &&
        (autoLoadEnabled ? (
          <div
            ref={sentinelRef}
            className="mt-4 flex w-full items-center justify-center py-2"
          >
            <LoadingCandles className="min-h-[80px]" label="Carregando mais análises..." />
          </div>
        ) : (
          <div className="flex justify-center pt-4">
            <Button
              type="button"
              variant="outline"
              disabled={loadingMore}
              onClick={handleLoadMore}
            >
              {loadingMore
                ? 'Carregando...'
                : hasMoreToShow
                  ? 'Ver mais'
                  : 'Carregar mais análises'}
            </Button>
          </div>
        ))}
    </div>
  )
}
